import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-shim";
import { useRef, useState, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Check, ChevronLeft, LogOut, MapPin, Camera as CameraIcon, Cloud, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getMe, updateMyAvatar, setMyMemberColor } from "@/lib/profile.functions";
import { getMyTrip, getTripMembers } from "@/lib/trip.functions";
import { resizeImageToJpeg } from "@/lib/image-resize";
import { TRAVELER_PALETTE, colorForUser, nameForColor } from "@/lib/traveler-colors";
import { isNative } from "@/lib/native/platform";
import { Camera } from "@capacitor/camera";
import { Filesystem } from "@capacitor/filesystem";
import {
  checkLocationPermission,
  checkPhotoLibraryPermission,
  type PermissionState,
} from "@/lib/native/permissions";
import { useOutboxStats } from "@/lib/native/use-outbox-stats";
import { BuildInfoLine } from "@/components/BuildInfoLine";
import { cn } from "@/lib/utils";
import {
  RoavaPage,
  RoavaCard,
  PrimaryButton,
  SecondaryButton,
  TextButton,
  StatusChip,
  type StatusChipVariant,
} from "@/components/roava";
import { TravelerColorPickerCard } from "@/components/profile/TravelerColorPickerCard";
import { AiAvatarSheet } from "@/components/profile/AiAvatarSheet";
import { SupportRoavaCard } from "@/components/support/SupportRoavaCard";
import { PrivacyCard } from "@/components/PrivacyCard";
import { SampleTripCard } from "@/components/onboarding/SampleTripCard";
import { ActiveTripCard } from "@/components/onboarding/ActiveTripCard";
import { Sparkles } from "lucide-react";

import { StorageSyncCard } from "@/components/storage/StorageSyncCard";
import { SyncOutboxCard } from "@/components/SyncOutboxCard";
import { LanguageCard } from "@/components/profile/LanguageCard";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function base64ToBlob(b64: string, mime: string): Blob {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

function mimeToExt(mime: string): string {
  if (mime.includes("heic")) return "heic";
  if (mime.includes("heif")) return "heif";
  if (mime.includes("png")) return "png";
  if (mime.includes("webp")) return "webp";
  return "jpg";
}


import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Trans } from "react-i18next";

export const Route = createFileRoute("/_authenticated/profile")({
  head: () =>
    pageMeta({
      title: "Account & Traveler Profile — Roava",
      description: "Your account, traveler identity, map appearance, and setup status.",
      path: "/profile",
    }),
  component: ProfilePage,
});

type PermStatus = {
  state: PermissionState | "unknown";
};

function permToChip(t: TFunction, s: PermStatus["state"]): { variant: StatusChipVariant; label: string } {
  switch (s) {
    case "granted":
      return { variant: "success", label: t("profile.perm_granted") };
    case "limited":
      return { variant: "warning", label: t("profile.perm_limited") };
    case "denied":
      return { variant: "danger", label: t("profile.perm_denied") };
    case "prompt":
      return { variant: "neutral", label: t("profile.perm_prompt") };
    case "unsupported":
      return { variant: "neutral", label: t("profile.perm_unsupported") };
    default:
      return { variant: "neutral", label: t("profile.perm_unknown") };
  }
}


function ProfilePage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const nav = useNavigate();
  const meFn = useServerFn(getMe);

  const tripFn = useServerFn(getMyTrip);
  const membersFn = useServerFn(getTripMembers);
  const updateAvatarFn = useServerFn(updateMyAvatar);
  const setColorFn = useServerFn(setMyMemberColor);

  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => meFn({}) });
  const { data: tripData } = useQuery({ queryKey: ["my-trip"], queryFn: () => tripFn({}) });
  const tripId = tripData?.trip?.id ?? null;
  const isAdmin = !!tripData?.isAdmin;

  const { data: membersData } = useQuery({
    queryKey: ["trip-members", tripId],
    queryFn: () => membersFn({ data: { tripId: tripId! } }),
    enabled: !!tripId,
    refetchOnMount: "always",
  });

  const myMember = membersData?.members?.find((m) => m.userId === me?.id);
  const fallbackColor = me?.id ? colorForUser(me.id) : TRAVELER_PALETTE[0];
  const [color, setColor] = useState<string>(myMember?.color ?? fallbackColor);
  const [showCustom, setShowCustom] = useState(false);
  // Seed picker from server ONCE on first members load.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (!membersData) return;
    seededRef.current = true;
    if (myMember?.color) setColor(myMember.color);
    else if (me?.id) setColor(colorForUser(me.id));
  }, [membersData, myMember?.color, me?.id]);

  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  // Permission status (best-effort; native only)
  const [locPerm, setLocPerm] = useState<PermStatus>({ state: "unknown" });
  const [photoPerm, setPhotoPerm] = useState<PermStatus>({ state: "unknown" });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const l = await checkLocationPermission();
        if (!cancelled) setLocPerm({ state: l });
      } catch { /* ignore */ }
      try {
        const p = await checkPhotoLibraryPermission();
        if (!cancelled) setPhotoPerm({ state: p });
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const outbox = useOutboxStats();

  const colorMut = useMutation({
    mutationFn: async (c: string | null) => {
      const res = await setColorFn({ data: { tripId: tripId!, color: c } });
      return res;
    },
    onMutate: async (c: string | null) => {
      await qc.cancelQueries({ queryKey: ["trip-members", tripId] });
      const prev = qc.getQueryData(["trip-members", tripId]);
      qc.setQueryData(["trip-members", tripId], (old: any) => {
        if (!old?.members) return old;
        return {
          ...old,
          members: old.members.map((m: any) =>
            m.userId === me?.id ? { ...m, color: c } : m,
          ),
        };
      });
      return { prev };
    },
    onSuccess: () => toast.success(t("profile.toast_color_saved")),
    onError: (e: any, _c, ctx: any) => {
      if (ctx?.prev) qc.setQueryData(["trip-members", tripId], ctx.prev);
      toast.error(e?.message ?? t("profile.toast_color_failed"));
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["trip-members", tripId] });
    },
  });


  const removeAvatarMut = useMutation({
    mutationFn: () => updateAvatarFn({ data: { avatarUrl: null } }),
    onSuccess: () => {
      toast.success(t("profile.toast_photo_removed"));
      qc.setQueryData(["me"], (old: any) =>
        old ? { ...old, avatarUrl: null } : old,
      );
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["trip-members", tripId] });
    },
    onError: (e: any) => toast.error(e?.message ?? t("profile.toast_photo_remove_failed")),
  });



  /** Upload a Blob as the user's avatar and update profile.avatar_url. */
  async function uploadAvatarBlob(blob: Blob, suggestedExt?: string) {
    let { data: sessionData } = await supabase.auth.getSession();
    let session = sessionData?.session;
    if (!session) {
      const { data: refreshed } = await supabase.auth.refreshSession();
      session = refreshed?.session ?? null;
    }
    if (!session?.user) {
      throw new Error("Not signed in");
    }
    const uid = session.user.id;
    const mime = blob.type || "image/jpeg";
    const ext = suggestedExt ?? mimeToExt(mime);
    const path = `${uid}/avatar-${Date.now()}.${ext}`;

    const doUpload = () =>
      supabase.storage.from("avatars").upload(path, blob, { contentType: mime });

    let { error: upErr } = await doUpload();
    if (upErr && /row-level security|unauthorized|jwt/i.test(upErr.message)) {
      await supabase.auth.refreshSession();
      ({ error: upErr } = await doUpload());
    }
    if (upErr) throw new Error(upErr.message);

    const { data: pub } = supabase.storage.from("avatars").getPublicUrl(path);
    const publicUrl = `${pub.publicUrl}?v=${Date.now()}`;
    await updateAvatarFn({ data: { avatarUrl: publicUrl } });
    qc.setQueryData(["me"], (old: any) =>
      old ? { ...old, avatarUrl: publicUrl } : old,
    );
    qc.invalidateQueries({ queryKey: ["me"] });
    qc.invalidateQueries({ queryKey: ["trip-members", tripId] });
    return publicUrl;
  }

  async function handleFile(file: File) {
    if (file.size > 8 * 1024 * 1024) {
      toast.error(t("profile.toast_image_too_large"));
      return;
    }
    setUploading(true);
    try {
      const blob = await resizeImageToJpeg(file, 512, 0.85);
      await uploadAvatarBlob(blob, "jpg");
      toast.success(t("profile.toast_photo_updated"));
    } catch (e: any) {
      toast.error(e?.message ?? t("profile.toast_photo_upload_failed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  const [aiAvatarOpen, setAiAvatarOpen] = useState(false);

  async function handleAiAvatar(blob: Blob) {
    // Re-encode to JPEG via the same resize path used by uploads, so storage
    // and dimensions are consistent with the regular upload flow.
    const jpeg = await resizeImageToJpeg(
      new File([blob], "avatar.png", { type: blob.type || "image/png" }),
      512,
      0.9,
    );
    await uploadAvatarBlob(jpeg, "jpg");
    toast.success(t("profile.toast_ai_avatar_saved"));
  }




  async function pickAndUpload() {
    if (uploading) return;
    if (!isNative()) {
      fileRef.current?.click();
      return;
    }
    try {
      const res = await Camera.pickImages({ quality: 90, limit: 1 });
      const ph = res.photos[0];
      if (!ph) return;
      const fsPath = ph.path || ph.webPath;
      if (!fsPath) {
        toast.error(t("profile.toast_photo_path_failed"));
        return;
      }
      const rawName = fsPath.split("/").pop() || `photo.${ph.format || "jpg"}`;
      const ext = (rawName.split(".").pop() || ph.format || "jpg").toLowerCase();
      const mime =
        ext === "heic" || ext === "heif"
          ? "image/heic"
          : ext === "png"
            ? "image/png"
            : ext === "webp"
              ? "image/webp"
              : "image/jpeg";
      const { data } = await Filesystem.readFile({ path: fsPath });
      const b64 = typeof data === "string" ? data : await (data as Blob).text();
      const blob = base64ToBlob(b64, mime);
      const file = new File([blob], rawName, { type: mime });
      await handleFile(file);
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      if (/cancel/i.test(msg)) return;
      toast.error(msg || t("profile.toast_photo_pick_failed"));
    }
  }

  async function handleSignOut() {
    // 5.8: non-blocking warning if pending uploads exist. Never auto-syncs,
    // never discards. RLS scopes the count to the current user automatically.
    try {
      if (me?.id && tripId) {
        const { count } = await supabase
          .from("media_assets")
          .select("id", { count: "exact", head: true })
          .eq("user_id", me.id)
          .eq("trip_id", tripId)
          .in("upload_status", ["queued", "uploading", "failed"]);
        if ((count ?? 0) > 0) {
          toast(t("profile.toast_pending_uploads"));
        }
      }
    } catch { /* noop */ }
    try { await (await import("@/lib/cache/clearOnSignOut")).clearMediaCacheForCurrentUser(); } catch { /* noop */ }
    (await import("@/lib/offline/offline-auth")).markUserInitiatedSignOut();
    await supabase.auth.signOut();
    nav({ to: "/login" });
  }

  const initials = (me?.displayName ?? me?.email ?? "?").slice(0, 1).toUpperCase();
  const colorName = nameForColor(myMember?.color ?? null);
  const stagedColorName = nameForColor(color);

  const role = !tripId
    ? t("profile.role_no_trip")
    : isAdmin
      ? t("profile.role_admin")
      : t("profile.role_member");

  const locChip = permToChip(t, locPerm.state);
  const photoChip = permToChip(t, photoPerm.state);
  // Background tracking is not directly queryable from JS in our shim; reflect
  // location "Always" via the same permission state when known.
  const bgChip =
    locPerm.state === "granted"
      ? { variant: "success" as StatusChipVariant, label: t("profile.bg_available") }
      : locPerm.state === "unknown"
        ? { variant: "neutral" as StatusChipVariant, label: t("profile.perm_unknown") }
        : { variant: "neutral" as StatusChipVariant, label: t("profile.bg_needs_location") };

  const syncChip: { variant: StatusChipVariant; label: string } =
    outbox.draining
      ? { variant: "info", label: t("profile.sync_syncing") }
      : outbox.queued > 0
        ? { variant: "warning", label: t("profile.sync_pending", { n: outbox.queued }) }
        : outbox.lastSyncError
          ? { variant: "danger", label: t("profile.sync_failed") }
          : { variant: "success", label: t("profile.sync_up_to_date") };

  return (
    <RoavaPage
      title={t("profile.page_title")}
      subtitle={t("profile.page_subtitle")}

    >
      <div className="grid gap-4 min-w-0 w-full max-w-full overflow-x-clip">
        {/* 1. Account */}
        <RoavaCard title={t("profile.card_account")}>
          <div className="flex items-center gap-4">
            <Avatar className="size-16">
              {me?.avatarUrl && <AvatarImage src={me.avatarUrl} alt={t("profile.your_avatar_alt")} />}
              <AvatarFallback className="text-lg">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[var(--roava-text)] truncate">
                {me?.displayName ?? t("profile.traveler_fallback")}
              </p>
              {me?.email && (
                <p className="text-xs text-[var(--roava-text-muted)] truncate">{me.email}</p>
              )}
              <div className="mt-1.5">
                <StatusChip variant={isAdmin ? "info" : "neutral"}>{role}</StatusChip>
              </div>
            </div>
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
            }}
          />

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <PrimaryButton
              type="button"
              size="sm"
              onClick={() => void pickAndUpload()}
              disabled={uploading}
            >
              {uploading ? t("profile.uploading") : me?.avatarUrl ? t("profile.change_photo") : t("profile.upload_photo")}
            </PrimaryButton>
            <SecondaryButton
              type="button"
              size="sm"
              onClick={() => setAiAvatarOpen(true)}
              disabled={uploading}
              className="gap-1.5"
            >
              <Sparkles className="size-3.5" />
              {t("profile.create_ai_avatar")}
            </SecondaryButton>
            {me?.avatarUrl && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <TextButton
                    type="button"
                    size="sm"
                    disabled={removeAvatarMut.isPending || uploading}
                    className="text-[var(--roava-red)]"
                  >
                    {t("profile.remove_photo")}
                  </TextButton>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("profile.remove_photo_title")}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("profile.remove_photo_body")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("profile.cancel")}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => removeAvatarMut.mutate()}
                      className="bg-[var(--roava-red)] hover:bg-[var(--roava-red)]/90"
                    >
                      {t("profile.remove")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>

          <AiAvatarSheet
            open={aiAvatarOpen}
            onOpenChange={setAiAvatarOpen}
            tripId={tripId}
            onUseAvatar={handleAiAvatar}
          />



          <div className="mt-4 grid gap-3">
            <SecondaryButton
              onClick={handleSignOut}
              className="h-11 rounded-2xl w-full justify-center gap-2"
            >
              <LogOut className="size-4" />
              {t("profile.sign_out")}
            </SecondaryButton>
            <div className="rounded-2xl border border-[var(--roava-border)] bg-[var(--roava-card)]/60 px-3 py-1.5 max-w-full w-fit self-start">
              <BuildInfoLine />
            </div>
            <button
              type="button"
              onClick={() => {
                void import("@/lib/stale-build").then(({ forceRefreshNow }) =>
                  forceRefreshNow(),
                );
              }}
              className="text-[10px] text-muted-foreground/70 underline underline-offset-2 self-start"
            >
              {t("profile.force_refresh")}
            </button>

          </div>
        </RoavaCard>

        {/* Sync now — surfaced here so travelers don't have to dig through Offline Control. */}
        <SyncOutboxCard />

        {/* Language */}
        <LanguageCard profileLocale={me?.locale ?? null} />



        {/* Storage & Sync — 5.8: sync push + safe cache/offline controls. */}
        <RoavaCard title={t("profile.card_storage_sync")}>
          <StorageSyncCard variant="full" userId={me?.id ?? null} tripId={tripId ?? null} />
        </RoavaCard>




        {/* 2. Map Appearance (includes large traveler preview) */}
        {tripId ? (
          <TravelerColorPickerCard
            userId={me?.id ?? ""}
            displayName={me?.displayName ?? t("profile.traveler_fallback")}
            avatarUrl={me?.avatarUrl ?? null}
            serverColor={myMember?.color ?? null}
            otherMembers={
              membersData?.members
                ?.filter((m) => m.userId !== me?.id)
                .map((m) => ({
                  userId: m.userId,
                  displayName: m.displayName,
                  color: m.color ?? null,
                })) ?? []
            }
            onSave={(c: string | null) => colorMut.mutate(c)}
            isSaving={colorMut.isPending}
          />
        ) : (
          <RoavaCard title={t("profile.card_map_appearance")}>
            <p className="text-sm text-[var(--roava-text-muted)]">
              {t("profile.join_to_choose_color")}
            </p>
          </RoavaCard>
        )}

        {/* 4. Setup & Permissions */}
        <RoavaCard
          title={t("profile.card_setup_title")}
          description={t("profile.card_setup_desc")}
        >
          <ul className="grid gap-2">
            <SetupRow
              icon={<MapPin className="size-4" />}
              label={t("profile.row_location")}
              chip={locChip}
            />
            <SetupRow
              icon={<MapPin className="size-4" />}
              label={t("profile.row_background")}
              chip={bgChip}
            />
            <SetupRow
              icon={<CameraIcon className="size-4" />}
              label={t("profile.row_photo")}
              chip={photoChip}
            />
            <SetupRow
              icon={<Cloud className="size-4" />}
              label={t("profile.row_sync")}
              chip={syncChip}
            />
          </ul>
          <div className="mt-4">
            <Link
              to="/permissions"
              className="inline-flex items-center gap-1 text-sm font-medium text-[var(--roava-blue)] hover:underline"
            >
              {t("profile.open_full_permissions")}
            </Link>
          </div>
        </RoavaCard>

        {/* 5. Privacy & Sharing */}
        <PrivacyCard />

        {/* 6. Active trip switcher (hidden if user has only 1 trip) */}
        <ActiveTripCard />

        {/* 7. Sample trip */}
        <SampleTripCard />

        {/* 8. Support Roava — optional tips (Apple IAP) */}
        <SupportRoavaCard />

        {/* 9. Danger zone — Apple 5.1.1(v) one-tap in-app account deletion. */}
        <DangerZoneCard />







        <div className="pt-1">
          <Link
            to="/upload-mode"
            className="inline-flex items-center gap-1 text-sm font-medium text-[var(--roava-text-muted)] hover:text-[var(--roava-text)] transition-colors"
          >
            <ChevronLeft className="size-4" />
            {t("profile.back_to_upload_settings")}
          </Link>
        </div>

      </div>
    </RoavaPage>
  );
}

function SetupRow({
  icon,
  label,
  chip,
}: {
  icon: React.ReactNode;
  label: string;
  chip: { variant: StatusChipVariant; label: string };
}) {
  return (
    <li className="flex items-center gap-3 rounded-2xl border border-[var(--roava-border)] bg-[var(--roava-card)]/40 px-3 py-3">
      <span className="grid place-items-center size-8 rounded-full bg-[var(--roava-gray)] text-[var(--roava-text-muted)] shrink-0">
        {icon}
      </span>
      <span className="flex-1 min-w-0 text-sm font-medium text-[var(--roava-text)] truncate">
        {label}
      </span>
      <StatusChip variant={chip.variant}>{chip.label}</StatusChip>
    </li>
  );
}

// Apple 5.1.1(v) compliant in-app account deletion. One tap → confirm by
// typing DELETE → calls the `delete-account` edge function (service-role,
// JWT-verified) → clears local caches → signs out. No email required.
function DangerZoneCard() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  async function handleDelete() {
    if (confirmText.trim().toUpperCase() !== "DELETE") return;
    setBusy(true);
    try {
      const { error } = await supabase.functions.invoke("delete-account", {
        method: "POST",
      });
      if (error) throw error;

      try {
        await (await import("@/lib/cache/clearOnSignOut")).clearMediaCacheForCurrentUser();
      } catch { /* noop */ }
      try {
        (await import("@/lib/offline/offline-auth")).markUserInitiatedSignOut();
      } catch { /* noop */ }
      try {
        await supabase.auth.signOut();
      } catch { /* noop */ }

      toast.success(t("profile.toast_account_deleted"));
      setOpen(false);
      nav({ to: "/login" });
    } catch (e: any) {
      toast.error(e?.message || t("profile.toast_account_delete_failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <RoavaCard title={t("profile.card_danger")}>
      <div className="rounded-2xl border border-[var(--roava-red)]/40 bg-[var(--roava-red)]/5 p-4">
        <div className="flex items-start gap-3">
          <span className="grid place-items-center size-8 rounded-full bg-[var(--roava-red)]/15 text-[var(--roava-red)] shrink-0">
            <AlertTriangle className="size-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[var(--roava-text)]">{t("profile.danger_label")}</p>
            <p className="mt-1 text-xs text-[var(--roava-text-muted)]">
              {t("profile.danger_body")}
            </p>
          </div>
        </div>
        <div className="mt-3">
          <AlertDialog
            open={open}
            onOpenChange={(o) => {
              setOpen(o);
              if (!o) setConfirmText("");
            }}
          >
            <AlertDialogTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-2xl bg-[var(--roava-red)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--roava-red)]/90 disabled:opacity-60"
              >
                {t("profile.danger_button")}
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("profile.danger_dialog_title")}</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-3 text-sm">
                    <p>{t("profile.danger_dialog_intro")}</p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>{t("profile.danger_item_account")}</li>
                      <li>{t("profile.danger_item_trips")}</li>
                      <li>{t("profile.danger_item_media")}</li>
                      <li>{t("profile.danger_item_routes")}</li>
                      <li>{t("profile.danger_item_family")}</li>
                      <li>{t("profile.danger_item_notifs")}</li>
                    </ul>
                    <p className="font-semibold text-[var(--roava-text)]">
                      {t("profile.danger_irreversible")}
                    </p>
                    <div className="pt-2">
                      <label className="block text-xs font-medium text-[var(--roava-text-muted)] mb-1">
                        <Trans
                          i18nKey="profile.danger_type_to_confirm"
                          components={{ 1: <span className="font-mono font-semibold text-[var(--roava-text)]" /> }}
                        />
                      </label>
                      <Input
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="DELETE"
                        autoCapitalize="characters"
                        autoCorrect="off"
                        spellCheck={false}
                      />
                    </div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={busy}>{t("profile.cancel")}</AlertDialogCancel>
                <AlertDialogAction
                  disabled={busy || confirmText.trim().toUpperCase() !== "DELETE"}
                  onClick={(e) => {
                    e.preventDefault();
                    void handleDelete();
                  }}
                  className="bg-[var(--roava-red)] hover:bg-[var(--roava-red)]/90"
                >
                  {busy ? t("profile.danger_deleting") : t("profile.danger_button")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </RoavaCard>
  );
}


